import { Patient } from '../types/patient'
import { PracticeLocation } from '../types/practiceLocation'
import { formatDate } from '../utils/locale'
import './PatientInstructionsPrintView.css'

export interface PatientInstructionsPrintViewProps {
  patient: Patient
  patientInstructions: string
  instructionsGiven?: boolean
  instructionsGivenAt?: string
  /** Display name of who gave instructions (e.g. staff name). Falls back to instructionsGivenBy if not provided. */
  instructionsGivenByName?: string
  instructionsGivenBy?: string
  practiceLocation?: PracticeLocation
  logoUrl?: string | null
}

export function PatientInstructionsPrintView({
  patient,
  patientInstructions,
  instructionsGiven,
  instructionsGivenAt,
  instructionsGivenByName,
  instructionsGivenBy,
  practiceLocation,
  logoUrl,
}: PatientInstructionsPrintViewProps) {
  const address = patient.address
    ? `${patient.address.street || ''}, ${patient.address.city || ''}, ${patient.address.postalCode || ''}`.trim()
    : ''

  const primaryAddress = practiceLocation?.addresses?.find((addr) => addr.type === 'primary' && addr.isActive)
  const practiceAddress = primaryAddress
    ? [
        primaryAddress.addressLine1,
        primaryAddress.addressLine2,
        primaryAddress.city,
        primaryAddress.county,
        primaryAddress.postcode,
      ]
        .filter(Boolean)
        .join(', ')
    : ''

  const displayLogoUrl = (logoUrl ?? '').trim() || '/logo@0.5x.png'

  return (
    <div className="patient-instructions-print-view">
      {/* Logo and Practice Header */}
      <div className="pi-print-header">
        {displayLogoUrl && (
          <div className="pi-logo-container">
            <img src={displayLogoUrl} alt="Clinic logo" className="pi-logo" />
          </div>
        )}
        {(practiceLocation || practiceAddress) && (
          <div className="pi-practice-header">
            {practiceLocation?.practiceName && (
              <div className="pi-practice-name">{practiceLocation.practiceName}</div>
            )}
            {practiceAddress && <div className="pi-practice-address">{practiceAddress}</div>}
          </div>
        )}
      </div>

      <div className="pi-document-header">
        <h1 className="pi-title">Patient Instructions</h1>
        <div className="pi-date">Date: {formatDate(new Date().toISOString())}</div>
      </div>

      <div className="pi-body">
        {/* Patient Information */}
        <div className="pi-section pi-patient-section">
          <h2 className="pi-section-title">Patient Information</h2>
          <div className="pi-patient-info-grid">
            <div className="pi-info-item">
              <span className="pi-info-label">Name:</span>
              <span className="pi-info-value">
                {patient.firstName} {patient.lastName}
              </span>
            </div>
            <div className="pi-info-item">
              <span className="pi-info-label">Date of Birth:</span>
              <span className="pi-info-value">{formatDate(patient.dateOfBirth)}</span>
            </div>
            {patient.patientNumber && (
              <div className="pi-info-item">
                <span className="pi-info-label">Patient #:</span>
                <span className="pi-info-value">{patient.patientNumber}</span>
              </div>
            )}
            {address && (
              <div className="pi-info-item pi-info-item-full">
                <span className="pi-info-label">Address:</span>
                <span className="pi-info-value">{address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Patient Instructions */}
        <div className="pi-section pi-instructions-section">
          <h2 className="pi-section-title">Instructions</h2>
          <div className="pi-instructions-content">{patientInstructions || 'No instructions provided.'}</div>
          {instructionsGiven && (
            <div className="pi-instructions-meta">
              <span className="pi-instructions-badge">Instructions Given</span>
              {instructionsGivenAt && (
                <span className="pi-instructions-timestamp">
                  Given on {new Date(instructionsGivenAt).toLocaleString()}
                </span>
              )}
              {(instructionsGivenByName || instructionsGivenBy) && (
                <span className="pi-instructions-given-by">
                  by {instructionsGivenByName || instructionsGivenBy}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="pi-footer">
        <div className="pi-footer-note">
          This document was generated on {formatDate(new Date().toISOString())}
        </div>
      </div>
    </div>
  )
}
